/**
 * RAG Dialog - Hybrid RAG: SharePoint + Azure AI Search + Background Info
 */

import { appendMessage } from "../utils/helpers.js";
import { setUseRAGContext, getUseRAGContext } from "../services/chatService.js";
import { getMsalInstance, getAccount } from "../services/authService.js";
import {
    initSharePointService,
    getSharePointSites,
    getOneDriveFolders,
    getSiteDocuments,
    saveSharePointConfig,
    loadSharePointConfig,
    clearSharePointConfig,
    isSharePointConfigured
} from "../services/sharePointService.js";
import {
    checkRAGStatus,
    uploadDocument,
    listDocuments,
    deleteDocument,
    clearAllDocuments
} from "../services/ragService.js";
import { initBackgroundTab } from "./backgroundDialog.js";
import { getRagFeatures } from "../services/quotaService.js";

let ragDialogInitialized = false;
let azureSearchAvailable = false;

/**
 * Initialize RAG UI with MSAL instance
 */
export async function initRAGUI(msal, account) {
    // Initialize SharePoint service with current auth context
    if (msal && account) {
        initSharePointService(msal, account);
    }
    
    // Always show RAG button
    const ragButton = document.getElementById("rag-btn");
    if (ragButton) {
        ragButton.classList.remove("hidden");
    }
    
    // Apply feature flag visibility for SharePoint and Upload tabs
    const features = getRagFeatures();
    
    const sharepointTabBtn = document.querySelector('.rag-tab[data-tab="sharepoint"]');
    const uploadsTabBtn = document.querySelector('.rag-tab[data-tab="uploads"]');
    const sharepointTabContent = document.getElementById('sharepoint-tab');
    const uploadsTabContent = document.getElementById('uploads-tab');
    
    if (sharepointTabBtn) sharepointTabBtn.classList.toggle('hidden', !features.includes('cloud'));
    if (sharepointTabContent) sharepointTabContent.classList.toggle('hidden', !features.includes('cloud'));
    if (uploadsTabBtn) uploadsTabBtn.classList.toggle('hidden', !features.includes('upload'));
    if (uploadsTabContent) uploadsTabContent.classList.toggle('hidden', !features.includes('upload'));

    // Update description text based on which features are active
    const descEl = document.querySelector('.rag-description');
    const titleEl = document.querySelector('#rag-dialog .dialog-title');
    const toggleTextEl = document.querySelector('.rag-toggle-text');
    const hasCloud = features.includes('cloud');
    const hasUpload = features.includes('upload');
    if (descEl) {
        if (hasCloud && hasUpload) {
            descEl.textContent = 'Füge Hintergrundinformationen hinzu, verbinde Datenquellen oder lade Dokumente hoch, damit LEA auf deine Inhalte als Wissensquelle zugreifen kann.';
        } else if (hasCloud) {
            descEl.textContent = 'Füge Hintergrundinformationen hinzu oder verbinde SharePoint/OneDrive, damit LEA auf deine Dokumente als Wissensquelle zugreifen kann.';
        } else if (hasUpload) {
            descEl.textContent = 'Füge Hintergrundinformationen hinzu oder lade Dokumente hoch, damit LEA auf deine Inhalte als Wissensquelle zugreifen kann.';
        }
        else {
            descEl.textContent = '';
        }
    }
    if (titleEl) titleEl.textContent = 'Hintergrundinformationen';
    if (toggleTextEl) toggleTextEl.textContent = 'Informationen nutzen';
    
    // If the currently-active tab is now hidden, fall back to the background tab
    const activeTabBtn = document.querySelector('.rag-tab.active');
    if (activeTabBtn && activeTabBtn.classList.contains('hidden')) {
        activeTabBtn.classList.remove('active');
        const activeTabContent = document.getElementById(`${activeTabBtn.dataset.tab}-tab`);
        if (activeTabContent) activeTabContent.classList.remove('active');
        
        const backgroundTabBtn = document.querySelector('.rag-tab[data-tab="background"]');
        const backgroundTabContent = document.getElementById('background-tab');
        if (backgroundTabBtn) backgroundTabBtn.classList.add('active');
        if (backgroundTabContent) backgroundTabContent.classList.add('active');
    }
    
    // Initialize tab handlers
    initTabHandlers();
    
    // Initialize upload handlers
    initUploadHandlers();
    
    // Initialize EPJ edit handlers
    // Initialize delete button handlers (event delegation)
    initDeleteHandlers();
    
    // Initialize admin consent link
    initAdminConsentLink();
    
    ragDialogInitialized = true;
    return true;
}

/**
 * Initialize admin consent link
 */
function initAdminConsentLink() {
    const consentLink = document.getElementById('admin-consent-link');
    if (!consentLink) return;
    
    // Client ID from authConfig
    const clientId = "6b83cdad-4022-44f2-9f8e-038844af1f20";
    const redirectUri = encodeURIComponent(window.location.origin + "/index.html");
    
    // Admin consent URL
    const consentUrl = `https://login.microsoftonline.com/common/adminconsent?client_id=${clientId}&redirect_uri=${redirectUri}`;
    
    consentLink.href = consentUrl;
}

/**
 * Initialize tab navigation
 */
function initTabHandlers() {
    const tabs = document.querySelectorAll('.rag-tab');
    tabs.forEach(tab => {
        tab.addEventListener('click', () => {
            const tabId = tab.dataset.tab;
            
            // Update tab buttons
            tabs.forEach(t => t.classList.remove('active'));
            tab.classList.add('active');
            
            // Update tab content
            document.querySelectorAll('.rag-tab-content').forEach(content => {
                content.classList.remove('active');
            });
            document.getElementById(`${tabId}-tab`).classList.add('active');
            
            // Refresh content when switching tabs
            if (tabId === 'uploads') {
                refreshUploadsTab();
            } else if (tabId === 'background') {
                initBackgroundTab();
            }
        });
    });
}

/**
 * Initialize upload drag & drop and file input
 */
function initUploadHandlers() {
    const dropzone = document.getElementById('upload-dropzone');
    const fileInput = document.getElementById('file-input');
    const clearIndexBtn = document.getElementById('clear-index-btn');
    
    if (!dropzone || !fileInput) return;
    
    // Click to select file
    dropzone.addEventListener('click', () => fileInput.click());
    
    // File selected
    fileInput.addEventListener('change', (e) => {
        if (e.target.files.length > 0) {
            handleFileUpload(e.target.files[0]);
        }
    });
    
    // Clear index button
    if (clearIndexBtn) {
        clearIndexBtn.addEventListener('click', async () => {
            const confirmed = await showClearIndexConfirmDialog();
            if (confirmed) {
                await handleClearIndex();
            }
        });
    }
    
    // Drag & drop
    dropzone.addEventListener('dragover', (e) => {
        e.preventDefault();
        dropzone.classList.add('dragover');
    });
    
    dropzone.addEventListener('dragleave', () => {
        dropzone.classList.remove('dragover');
    });
    
    dropzone.addEventListener('drop', (e) => {
        e.preventDefault();
        dropzone.classList.remove('dragover');
        
        if (e.dataTransfer.files.length > 0) {
            handleFileUpload(e.dataTransfer.files[0]);
        }
    });
}

/**
 * Initialize delete button handlers using event delegation on document body
 */
function initDeleteHandlers() {
    // Use event delegation on body to catch dynamically added buttons
    document.body.addEventListener('click', async (e) => {
        // Check if clicked element or its parent is a delete button
        let target = e.target;
        
        // If clicked on icon inside button, get the button
        if (target.tagName === 'I' && target.classList.contains('ms-Icon')) {
            target = target.parentElement;
        }
        
        if (target && target.classList && target.classList.contains('document-delete')) {
            e.preventDefault();
            e.stopPropagation();
            
            const fileName = target.dataset.filename;
            
            console.log('Delete button clicked, fileName:', fileName);
            
            if (fileName) {
                await handleDeleteDocument(fileName);
            } else {
                console.error('No filename found on delete button');
            }
        }
    });
    
    console.log('Delete handlers initialized');
}

/**
 * Handle file upload
 */
async function handleFileUpload(file) {
    const progressSection = document.getElementById('upload-progress');
    const progressFill = document.getElementById('progress-fill');
    const progressText = document.getElementById('progress-text');
    
    progressSection.classList.remove('hidden');
    
    try {
        const result = await uploadDocument(file, (progress, status) => {
            progressFill.style.width = `${progress}%`;
            progressText.textContent = status;
        });
        
        appendMessage(`✅ "${file.name}" erfolgreich hochgeladen!`, false);
        
        // Small delay to allow Azure Search to index the document
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Refresh document list
        await loadDocumentsList();
        
        // Reset progress after delay
        setTimeout(() => {
            progressSection.classList.add('hidden');
            progressFill.style.width = '0%';
        }, 2000);
        
    } catch (error) {
        console.error('Upload error:', error);
        progressText.textContent = `Fehler: ${error.message}`;
        progressFill.style.width = '0%';
        
        setTimeout(() => {
            progressSection.classList.add('hidden');
        }, 3000);
    }
    
    // Reset file input
    document.getElementById('file-input').value = '';
}

/**
 * Handle clearing the entire index
 */
async function handleClearIndex() {
    const clearBtn = document.getElementById('clear-index-btn');
    const originalText = clearBtn.innerHTML;
    
    try {
        clearBtn.disabled = true;
        clearBtn.innerHTML = '<span>⏳ Lösche...</span>';
        
        const result = await clearAllDocuments();
        
        console.log('Clear all documents result:', result);
        
        if (result.chunksDeleted === 0) {
            appendMessage('ℹ️ Keine Dokumente zum Löschen gefunden', false);
        } else {
            appendMessage(`✅ ${result.chunksDeleted} Chunks erfolgreich gelöscht!`, false);
        }
        
        await loadDocumentsList();
        
        clearBtn.innerHTML = '<span>✅ Gelöscht</span>';
        setTimeout(() => {
            clearBtn.innerHTML = originalText;
            clearBtn.disabled = false;
        }, 2000);
        
    } catch (error) {
        console.error('Clear index error:', error);
        console.error('Error details:', {
            message: error.message,
            stack: error.stack
        });
        
        appendMessage(`❌ Fehler beim Löschen: ${error.message}`, false);
        
        clearBtn.innerHTML = originalText;
        clearBtn.disabled = false;
    }
}

/**
 * Refresh uploads tab content
 */
async function refreshUploadsTab() {
    await checkAzureSearchStatus();
    await loadDocumentsList();
}

/**
 * Check Azure AI Search status
 */
async function checkAzureSearchStatus() {
    const statusEl = document.getElementById('azure-search-status');
    if (!statusEl) return;
    
    statusEl.innerHTML = '<span class="status-icon">◌</span><span class="status-text">Prüfe Status...</span>';
    statusEl.classList.remove('available', 'unavailable');
    
    try {
        const status = await checkRAGStatus();
        // Backend returns { enabled: true/false, indexName: "..." }
        azureSearchAvailable = status.available === true;
        
        if (azureSearchAvailable) {
            statusEl.classList.add('available');
            statusEl.innerHTML = '<span class="status-icon">●</span><span class="status-text">Azure AI Search verfügbar</span>';
        } else {
            statusEl.classList.add('unavailable');
            statusEl.innerHTML = `<span class="status-icon">○</span><span class="status-text">Azure AI Search nicht konfiguriert</span>`;
        }
    } catch (error) {
        statusEl.classList.add('unavailable');
        statusEl.innerHTML = '<span class="status-icon">○</span><span class="status-text">Service nicht erreichbar</span>';
        azureSearchAvailable = false;
    }
}

/**
 * Load and display documents list
 */
async function loadDocumentsList() {
    const listEl = document.getElementById('documents-list');
    if (!listEl) return;
    
    listEl.innerHTML = '<p class="no-documents">Lade Dokumente...</p>';
    
    try {
        const documents = await listDocuments();
        
        if (!documents || documents.length === 0) {
            listEl.innerHTML = '<p class="no-documents">Keine Dokumente hochgeladen</p>';
            return;
        }
        
        listEl.innerHTML = documents.map(doc => {
            const fileName = doc.fileName || doc.name || 'Unbekannt';
            const uploadDate = doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString('de-DE') : '';
            const chunkCount = doc.totalChunks || doc.chunkCount || doc.chunks || '?';
            
            return `
                <div class="document-item" data-filename="${escapeHtml(fileName)}">
                    <i class="ms-Icon ms-Icon--Document"></i>
                    <div class="document-info">
                        <div class="document-name">${escapeHtml(fileName)}</div>
                        <div class="document-meta">${uploadDate} · ${chunkCount} Chunks</div>
                    </div>
                    <button class="document-delete" type="button" title="Löschen" data-filename="${escapeHtml(fileName)}">
                        <i class="ms-Icon ms-Icon--Delete"></i>
                    </button>
                </div>
            `;
        }).join('');
        
    } catch (error) {
        console.error('Error loading documents:', error);
        listEl.innerHTML = '<p class="no-documents">Fehler beim Laden</p>';
    }
}

/**
 * Delete document handler
 */
async function handleDeleteDocument(fileName) {
    // Use custom confirm dialog instead of window.confirm (not supported in Office Add-ins)
    const confirmed = await showDeleteConfirmDialog(fileName);
    if (!confirmed) return;
    
    try {
        await deleteDocument(fileName);
        appendMessage(`🗑️ "${fileName}" gelöscht`, false);
        await loadDocumentsList();
    } catch (error) {
        console.error('Delete error:', error);
        appendMessage(`❌ Fehler beim Löschen: ${error.message}`, false);
    }
}

/**
 * Show a custom confirm dialog for clear index
 */
function showClearIndexConfirmDialog() {
    return new Promise((resolve) => {
        try {
            // Ensure document.body exists
            if (!document.body) {
                console.error('document.body is not available');
                resolve(false);
                return;
            }
            
            let dialog = document.getElementById('clear-index-confirm-dialog');
            if (!dialog) {
                dialog = document.createElement('div');
                dialog.id = 'clear-index-confirm-dialog';
                dialog.className = 'confirm-dialog-overlay';
                dialog.innerHTML = `
                    <div class="confirm-dialog-box">
                        <h3 style="color: #d13438; margin-top: 0;">⚠️ Index leeren?</h3>
                        <p>Wirklich <strong>ALLE Dokumente</strong> aus dem Index löschen?</p>
                        <p style="color: #666; font-size: 0.9em;">Dies kann nicht rückgängig gemacht werden.</p>
                        <div class="confirm-dialog-buttons">
                            <button id="clear-index-confirm-yes" class="ms-Button ms-Button--primary" style="background-color: #bb1e23; color: white;">
                                <span>Ja, alles löschen</span>
                            </button>
                            <button id="clear-index-confirm-no" class="ms-Button ms-Button--default">
                                <span>Abbrechen</span>
                            </button>
                        </div>
                    </div>
                `;
                document.body.appendChild(dialog);
            }
            
            dialog.style.display = 'flex';
            
            const yesBtn = dialog.querySelector('#clear-index-confirm-yes');
            const noBtn = dialog.querySelector('#clear-index-confirm-no');
            
            if (!yesBtn || !noBtn) {
                console.error('Dialog buttons not found');
                resolve(false);
                return;
            }
            
            const cleanup = () => {
                dialog.style.display = 'none';
                yesBtn.removeEventListener('click', onYes);
                noBtn.removeEventListener('click', onNo);
            };
            
            const onYes = () => {
                cleanup();
                resolve(true);
            };
            
            const onNo = () => {
                cleanup();
                resolve(false);
            };
            
            yesBtn.addEventListener('click', onYes);
            noBtn.addEventListener('click', onNo);
        } catch (error) {
            console.error('Error showing clear index confirm dialog:', error);
            resolve(false);
        }
    });
}

/**
 * Show a custom confirm dialog for delete (Office Add-ins don't support window.confirm)
 */
function showDeleteConfirmDialog(fileName) {
    return new Promise((resolve) => {
        // Check if confirm dialog exists, if not create it
        let dialog = document.getElementById('delete-confirm-dialog');
        if (!dialog) {
            dialog = document.createElement('div');
            dialog.id = 'delete-confirm-dialog';
            dialog.className = 'confirm-dialog-overlay';
            dialog.innerHTML = `
                <div class="confirm-dialog-box">
                    <p id="delete-confirm-message"></p>
                    <div class="confirm-dialog-buttons">
                        <button id="delete-confirm-yes" class="ms-Button ms-Button--primary" style="background-color: #bb1e23; color: white;">
                            <span>Löschen</span>
                        </button>
                        <button id="delete-confirm-no" class="ms-Button ms-Button--default">
                            <span>Abbrechen</span>
                        </button>
                    </div>
                </div>
            `;
            document.body.appendChild(dialog);
        }
        
        document.getElementById('delete-confirm-message').textContent = `"${fileName}" wirklich löschen?`;
        dialog.classList.remove('hidden');
        dialog.style.display = 'flex';
        
        const yesBtn = document.getElementById('delete-confirm-yes');
        const noBtn = document.getElementById('delete-confirm-no');
        
        const cleanup = () => {
            dialog.classList.add('hidden');
            dialog.style.display = 'none';
            yesBtn.removeEventListener('click', onYes);
            noBtn.removeEventListener('click', onNo);
        };
        
        const onYes = () => {
            cleanup();
            resolve(true);
        };
        
        const onNo = () => {
            cleanup();
            resolve(false);
        };
        
        yesBtn.addEventListener('click', onYes);
        noBtn.addEventListener('click', onNo);
    });
}

/**
 * Open RAG documents dialog
 */
export async function openRAGDialog() {
    const dialog = document.getElementById("rag-dialog");
    if (!dialog) return;

    dialog.classList.remove("hidden");
    
    // Sync toggle state
    const toggle = document.getElementById("rag-context-toggle");
    if (toggle) {
        toggle.checked = getUseRAGContext();
    }
    
    // Update SharePoint status
    updateSharePointStatus();
    
    // Check Azure Search status for uploads tab
    checkAzureSearchStatus();
    loadDocumentsList();

    // Initialize background tab since it is the default active tab
    initBackgroundTab();
}

/**
 * Handle RAG context toggle change
 */
export function handleRAGToggleChange(event) {
    const enabled = event.target.checked;
    setUseRAGContext(enabled);
    
    if (enabled) {
        appendMessage("ℹ️ Hintergrundinformationen werden jetzt bei Anfragen genutzt.", false);
    } else {
        appendMessage("ℹ️ Hintergrundinformationen werden jetzt nicht mehr genutzt.", false);
    }
}

/**
 * Close RAG dialog
 */
export function closeRAGDialog() {
    const dialog = document.getElementById("rag-dialog");
    if (dialog) {
        dialog.classList.add("hidden");
    }
}

/**
 * Update SharePoint connection status in UI
 */
function updateSharePointStatus() {
    const statusEl = document.getElementById("sharepoint-status");
    const connectBtn = document.getElementById("connect-sharepoint-btn");
    const configSection = document.getElementById("sharepoint-config");
    const connectedInfo = document.getElementById("sharepoint-connected-info");
    
    const config = loadSharePointConfig();
    
    if (config && config.driveId) {
        // Connected state
        statusEl.classList.add("connected");
        statusEl.innerHTML = '<span class="status-icon">●</span><span class="status-text">Verbunden</span>';
        
        connectBtn.classList.add("hidden");
        configSection.classList.add("hidden");
        connectedInfo.classList.remove("hidden");
        
        // Show connected source name
        const sourceName = document.getElementById("connected-source-name");
        const folderPath = document.getElementById("connected-folder-path");
        
        if (config.sourceType === "onedrive") {
            sourceName.textContent = "OneDrive verbunden";
        } else {
            sourceName.textContent = config.siteName || "SharePoint verbunden";
        }
        
        folderPath.textContent = config.folderPath || "/ (Stammverzeichnis)";
    } else {
        // Not connected state
        statusEl.classList.remove("connected");
        statusEl.innerHTML = '<span class="status-icon">○</span><span class="status-text">Nicht verbunden</span>';
        
        connectBtn.classList.remove("hidden");
        configSection.classList.add("hidden");
        connectedInfo.classList.add("hidden");
    }
}

/**
 * Handle connect SharePoint button click
 */
export async function handleConnectSharePoint() {
    const connectBtn = document.getElementById("connect-sharepoint-btn");
    const configSection = document.getElementById("sharepoint-config");
    
    // Get current MSAL instance and account from authService
    const msalInstance = getMsalInstance();
    const currentAccount = getAccount();
    
    if (!msalInstance || !currentAccount) {
        appendMessage("❌ Bitte melde dich zuerst an.", false);
        console.error("MSAL not initialized:", { msalInstance: !!msalInstance, currentAccount: !!currentAccount });
        return;
    }
    
    // Re-initialize SharePoint service with current auth context
    initSharePointService(msalInstance, currentAccount);
    
    connectBtn.classList.add("hidden");
    configSection.classList.remove("hidden");
    
    // Set up source change handler (remove old listener first)
    const sourceSelect = document.getElementById("sharepoint-source");
    sourceSelect.removeEventListener("change", handleSourceChange);
    sourceSelect.addEventListener("change", handleSourceChange);
    
    // Trigger initial source selection
    handleSourceChange({ target: sourceSelect });
}

/**
 * Handle source type change (OneDrive vs SharePoint)
 */
async function handleSourceChange(event) {
    const sourceType = event.target.value;
    const siteSelectorGroup = document.getElementById("site-selector");
    const siteSelect = document.getElementById("sharepoint-site");
    
    if (sourceType === "sharepoint") {
        siteSelectorGroup.classList.remove("hidden");
        siteSelect.innerHTML = '<option value="">Lade Sites...</option>';
        
        try {
            const sites = await getSharePointSites();
            console.log("SharePoint sites loaded:", sites.length, sites);
            
            if (sites.length === 0) {
                siteSelect.innerHTML = '<option value="">Keine Sites gefunden - ggf. fehlen Berechtigungen</option>';
                appendMessage("⚠️ Keine SharePoint Sites gefunden. Möglicherweise benötigt die App Administrator-Zustimmung für Sites.Read.All", false);
            } else {
                // Don't include empty placeholder option when sites are loaded
                siteSelect.innerHTML = sites.map(site => 
                    `<option value="${site.id}" data-name="${escapeHtml(site.displayName)}">${escapeHtml(site.displayName)}</option>`
                ).join('');
                console.log("Sites successfully added to dropdown");
            }
        } catch (error) {
            console.error("Error loading sites:", error);
            siteSelect.innerHTML = '<option value="">Fehler beim Laden</option>';
            appendMessage(`❌ Fehler beim Laden der SharePoint Sites: ${error.message}. Bitte Administrator-Zustimmung über den Link oben erteilen.`, false);
        }
    } else {
        siteSelectorGroup.classList.add("hidden");
    }
    
    // Reset folder selection
    document.getElementById("selected-folder-path").textContent = "/ (Stammverzeichnis)";
}

/**
 * Handle folder browse button click
 */
export async function handleBrowseFolder() {
    const sourceType = document.getElementById("sharepoint-source").value;
    const siteSelect = document.getElementById("sharepoint-site");
    
    appendMessage("ℹ️ Ordner-Browser wird in einer zukünftigen Version verfügbar sein. Aktuell wird das Stammverzeichnis verwendet.", false);
    
    // TODO: Implement folder browser modal
    // For now, use root folder
}

/**
 * Handle save SharePoint configuration
 */
export async function handleSaveSharePointConfig() {
    const sourceType = document.getElementById("sharepoint-source").value;
    const siteSelect = document.getElementById("sharepoint-site");
    const folderPath = document.getElementById("selected-folder-path").textContent;
    
    let config = {
        sourceType: sourceType,
        folderPath: folderPath,
        folderId: null
    };
    
    if (sourceType === "onedrive") {
        // For OneDrive, we use the user's drive
        config.driveId = "me"; // Will be resolved at runtime
        config.siteName = "OneDrive";
    } else {
        const selectedSite = siteSelect.value;
        if (!selectedSite) {
            appendMessage("❌ Bitte wähle eine SharePoint Site aus.", false);
            return;
        }
        
        const selectedOption = siteSelect.options[siteSelect.selectedIndex];
        const siteName = selectedOption.getAttribute('data-name') || selectedOption.text;
        
        console.log("Saving SharePoint config:", {
            selectedSite,
            siteName,
            selectedIndex: siteSelect.selectedIndex
        });
        
        config.siteId = selectedSite;
        config.driveId = selectedSite; // Will be resolved to actual drive ID
        config.siteName = siteName;
    }
    
    saveSharePointConfig(config);
    updateSharePointStatus();
    
    appendMessage(`✅ ${config.siteName} erfolgreich verbunden!`, false);
}

/**
 * Handle disconnect SharePoint
 */
export function handleDisconnectSharePoint() {
    clearSharePointConfig();
    updateSharePointStatus();
    
    // Reset UI
    const configSection = document.getElementById("sharepoint-config");
    configSection.classList.add("hidden");
    
    appendMessage("ℹ️ SharePoint/OneDrive Verbindung getrennt.", false);
}

/**
 * Handle change SharePoint configuration
 */
export function handleChangeSharePointConfig() {
    const connectedInfo = document.getElementById("sharepoint-connected-info");
    const configSection = document.getElementById("sharepoint-config");
    
    connectedInfo.classList.add("hidden");
    configSection.classList.remove("hidden");
    
    // Reload current config into form
    const config = loadSharePointConfig();
    if (config) {
        document.getElementById("sharepoint-source").value = config.sourceType || "onedrive";
        handleSourceChange({ target: document.getElementById("sharepoint-source") });
    }
}

/**
 * Check if SharePoint is ready for RAG
 */
export function isSharePointReady() {
    return isSharePointConfigured() && msalInstance && currentAccount;
}

/**
 * Get current MSAL instance and account
 */
export function getMSALContext() {
    return { msalInstance, currentAccount };
}

// Helper function
function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}
